import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Grid2 as Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { listBrands, listCategories, listFeatureGroups, listTags } from '../../api/business/endpoints';
import type { AvailableLanguage, Tag } from '../../api/business/types';
import { flattenCategories } from '../../components/categoryTree';
import type { FlatCategory } from '../../components/categoryTree';
import { NO_FEATURE, withCategories } from '../../components/classificationDraft';
import type { ClassificationDraft, FeatureDraft } from '../../components/classificationDraft';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { useT } from '../../i18n/LanguageContext';

type ProductClassificationFieldsProps = {
  companyId: string;
  languages: Array<string>;
  available: Array<AvailableLanguage>;
  draft: ClassificationDraft;
  onChange: (next: ClassificationDraft) => void;
};

const CUSTOM = '__custom';

/**
 * Where the product sits in the company's taxonomy (R0030 WP-06): its categories picked from the tree with the main
 * one starred, its brand, its tags — a new one typed is made on save — and per feature group a value or its own
 * text in every content language.
 */
export const ProductClassificationFields = ({
  companyId,
  languages,
  available,
  draft,
  onChange,
}: ProductClassificationFieldsProps) => {
  const t = useT();
  const categories = useQuery({
    queryKey: ['categories', companyId],
    queryFn: () => listCategories(companyId),
  });
  const brands = useQuery({ queryKey: ['brands', companyId], queryFn: () => listBrands(companyId) });
  const tags = useQuery({ queryKey: ['tags', companyId], queryFn: () => listTags(companyId) });
  const groups = useQuery({
    queryKey: ['feature-groups', companyId],
    queryFn: () => listFeatureGroups(companyId),
  });

  const flat = flattenCategories(categories.data ?? []);
  const chosen = draft.categoryIds
    .map((id) => flat.find((one) => one.category.id === id))
    .filter((one): one is FlatCategory => one !== undefined);
  const allTags = tags.data ?? [];
  const chosenTags: Array<Tag | string> = [
    ...draft.tagIds
      .map((id) => allTags.find((tag) => tag.id === id))
      .filter((tag): tag is Tag => tag !== undefined),
    ...draft.newTags,
  ];
  const setFeature = (groupId: string, feature: FeatureDraft): void =>
    onChange({ ...draft, features: { ...draft.features, [groupId]: feature } });

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={flat}
        value={chosen}
        loading={categories.isLoading}
        isOptionEqualToValue={(option, value) => option.category.id === value.category.id}
        getOptionLabel={(option) => option.path.join(' › ')}
        onChange={(_event, value) =>
          onChange(
            withCategories(
              draft,
              value.map((one) => one.category.id),
            ),
          )
        }
        renderOption={({ key, ...props }, option, { selected }) => (
          <li key={key} {...props}>
            <Box sx={{ pl: (option.category.depth - 1) * 2, display: 'flex', alignItems: 'center' }}>
              <Checkbox size="small" checked={selected} sx={{ mr: 0.5 }} />
              {option.category.name}
            </Box>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...props } = getTagProps({ index });
            const main = option.category.id === draft.mainCategoryId;

            return (
              <Chip
                key={key}
                {...props}
                size="small"
                color={main ? 'primary' : 'default'}
                label={option.path.join(' › ')}
                icon={
                  <Tooltip title={main ? t('classification.mainChip') : t('classification.markMain')}>
                    {main ? (
                      <StarIcon fontSize="small" />
                    ) : (
                      <StarBorderIcon
                        fontSize="small"
                        sx={{ cursor: 'pointer' }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onChange({ ...draft, mainCategoryId: option.category.id });
                        }}
                      />
                    )}
                  </Tooltip>
                }
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('classification.categories')}
            helperText={t('classification.categoriesHint')}
          />
        )}
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField
            select
            fullWidth
            label={t('classification.brand')}
            value={draft.brandId}
            onChange={(event) => onChange({ ...draft, brandId: event.target.value })}
          >
            <MenuItem value="">{t('classification.noBrand')}</MenuItem>
            {(brands.data ?? [])
              .filter((brand) => brand.active || brand.id === draft.brandId)
              .map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 7 }}>
          <Autocomplete
            multiple
            freeSolo
            options={allTags}
            value={chosenTags}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            isOptionEqualToValue={(option, value) =>
              typeof option !== 'string' && typeof value !== 'string' && option.id === value.id
            }
            onChange={(_event, value) => {
              const tagIds: Array<string> = [];
              const newTags: Array<string> = [];

              for (const one of value) {
                if (typeof one !== 'string') {
                  tagIds.push(one.id);
                  continue;
                }

                const name = one.trim();
                const known = allTags.find(
                  (tag) => tag.name.toLocaleLowerCase('sk') === name.toLocaleLowerCase('sk'),
                );

                if (known) tagIds.push(known.id);
                else if (name !== '' && !newTags.includes(name)) newTags.push(name);
              }

              onChange({ ...draft, tagIds: [...new Set(tagIds)], newTags });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...props } = getTagProps({ index });

                return (
                  <Chip
                    key={key}
                    {...props}
                    size="small"
                    variant={typeof option === 'string' ? 'outlined' : 'filled'}
                    label={typeof option === 'string' ? option : option.name}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('classification.tags')}
                helperText={t('classification.tagsHint')}
              />
            )}
          />
        </Grid>
      </Grid>
      <Typography variant="subtitle2" color="text.secondary">
        {t('classification.features')}
      </Typography>
      {(groups.data ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('classification.noFeatureGroups')}
        </Typography>
      ) : null}
      {(groups.data ?? []).map((group) => {
        const feature = draft.features[group.id] ?? NO_FEATURE;
        const selected = feature.mode === 'custom' ? CUSTOM : feature.mode === 'value' ? feature.valueId : '';

        return (
          <Grid key={group.id} container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={group.name}
                value={selected}
                onChange={(event) => {
                  const value = event.target.value;

                  setFeature(
                    group.id,
                    value === ''
                      ? NO_FEATURE
                      : value === CUSTOM
                        ? { mode: 'custom', valueId: '', custom: feature.custom }
                        : { mode: 'value', valueId: value, custom: feature.custom },
                  );
                }}
              >
                <MenuItem value="">{t('classification.featureNone')}</MenuItem>
                {group.values.map((value) => (
                  <MenuItem key={value.id} value={value.id}>
                    {value.value}
                  </MenuItem>
                ))}
                <MenuItem value={CUSTOM}>{t('classification.featureCustom')}</MenuItem>
              </TextField>
            </Grid>
            {feature.mode === 'custom' ? (
              <Grid size={{ xs: 12, sm: 8 }}>
                <LocalisedTextField
                  size="small"
                  label={t('classification.customText')}
                  languages={languages}
                  available={available}
                  values={feature.custom}
                  onChange={(custom) => setFeature(group.id, { ...feature, custom })}
                  maxLength={255}
                />
              </Grid>
            ) : null}
          </Grid>
        );
      })}
    </Stack>
  );
};
